<script setup lang="ts">
import { Head, Link } from "@inertiajs/vue3"
import { CalendarIcon, IdCardIcon, PencilIcon } from "lucide-vue-next"

import Heading from "@/components/Heading.vue"
import BadgeStatus from "@/components/custom/Badge.vue"
import Table from "@/components/custom/Table.vue"
import { Badge } from "@/components/ui/badge"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import {
  editManagerEmployeePath,
  managerEmployeePath,
  managerEmployeesPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { ManagerEmployeesShow } from "@/types/serializers"

const props = defineProps<ManagerEmployeesShow>()

const columns: Array<Column<ManagerInsurance>> = [
  {
    key: "id",
    header: "ID",
    sortable: true,
  },
  {
    key: "type",
    header: "Tipo",
    sortable: true,
  },
  {
    key: "status",
    header: "Estado",
    sortable: true,
  },
  {
    key: "created_at",
    header: "Fecha de creación",
    right: true,
    sortable: true,
  },
]

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Perfiles de Empleados",
    href: managerEmployeesPath(),
  },
  {
    title: `Empleado ${props.employee.ref}`,
    href: managerEmployeePath({ id: props.employee.id }),
  },
]
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        title="Empleado"
        description="Actualiza la informacion principal del empleado"
      />
      <div class="border-border mt-6 rounded-md border p-4.5">
        <div class="flex justify-between">
          <div class="">
            <div class="flex items-center gap-2">
              <p class="text-2xl font-medium">{{ employee.full_name }}</p>
              <Badge>{{ employee.ref }}</Badge>
            </div>
            <p class="text-muted-foreground uppercase">
              {{ employee.job_title }} &bull; {{ employee.department }}
            </p>
            <p class="text-muted-foreground text-sm">{{ employee.email }}</p>
          </div>

          <Link
            class="border-primary bg-primary/5 text-primary flex h-fit items-center justify-center gap-2 rounded-sm border px-4 py-2"
            :href="editManagerEmployeePath(employee.id)"
          >
            <PencilIcon class="size-4" />
            Editar
          </Link>
        </div>

        <hr class="border-border mt-6" />

        <div class="mt-6 grid grid-cols-4 gap-4">
          <div class="flex items-center gap-2">
            <div
              class="bg-primary/5 flex size-10 items-center justify-center rounded-sm"
            >
              <IdCardIcon class="text-primary size-4" />
            </div>
            <p class="text-lg">{{ employee.dni }}</p>
          </div>

          <div class="col-span-2 flex items-center gap-2">
            <div
              class="bg-primary/5 flex size-10 items-center justify-center rounded-sm"
            >
              <CalendarIcon class="text-primary size-4" />
            </div>
            <div class="flex gap-1">
              <p class="text-lg">
                {{ dateFormatter(employee.start_date, {}, "date") }}
              </p>

              <span v-if="employee.termination_date">-</span>

              <p v-if="employee.termination_date" class="text-lg">
                {{ dateFormatter(employee.termination_date, {}, "date") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6">
        <Heading title="Solicitudes" class="mb-4" />
        <Table :data="requests" :columns="columns">
          <template #status-cell="{ row }">
            <BadgeStatus
              :title="row.reject_reason"
              class="w-fit"
              :status="row.status"
            />
          </template>

          <template #created_at-cell="{ row }">
            {{ dateFormatter(row.created_at, {}, "dateTime") }}
          </template>

          <template #empty>No hay solicitudes</template>
        </Table>

        <Pagination class="my-4 mt-auto px-6" />
      </div>
    </div>
  </AppLayout>
</template>
